// Codex "Intercept": a basic ability to tap a unit that is ALREADY on the
// cell an enemy moved onto (move-and-attack WITHOUT attacking). No movement
// is allowed. Only units sitting on the final cell qualify.
//
// Gates: untapped, opponent-controlled relative to the mover, on the cell,
// not disabled. Airborne. Can't intercept an Airborne mover unless the
// interceptor is Airborne or has Ranged. Stealth. A Stealth mover cannot
// be intercepted AT ALL (codex Intercept: "Units with Stealth cannot be
// intercepted, and units with Airborne can only be intercepted by enemies
// with Airborne or Ranged." The Airborne/Ranged exception belongs to the
// Airborne clause only).

import { hasKeyword } from './keywordQuery';
import { hasActiveStealth } from './stealth';
import { hasSummoningSickness } from './summoningSickness';
import { getRangedValue } from './ranged';
import { countsAsUnit } from './typeline';
import { unitFootprintCells } from './footprint';

/**
 * @param mover the unit that moved onto the cell without attacking
 * @param cell  { col, row } the mover's final cell
 * @param ctx   { meshes, cellAt }
 * @returns Array<cardInstance> eligible interceptors
 */
export function getInterceptCandidates(mover, cell, { meshes, cellAt }) {
  if (!mover || !cell) return [];
  if (hasActiveStealth(mover)) return []; // Stealth movers can't be intercepted
  const moverRotated = !!mover.rotated;
  const moverAirborne = hasKeyword(mover, 'airborne');
  // Intercept requires the interceptor to be in the mover's SAME location,
  // which is the same square AND the same region. A surface unit cannot
  // intercept an underground/underwater mover (and vice versa); only a
  // same-region unit already sitting on the destination qualifies (Bug 6).
  // null == surface.
  const moverRegion = mover.subsurface || null;

  const out = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || card.id === mover.id) continue;
    if (card.isSite) continue;
    // Only units intercept (codex Intercept: "tap a unit"). Relics and
    // other non-minion artifacts sitting on the cell never qualify.
    if (!countsAsUnit(card)) continue;
    // A multi-cell unit (an animated aura, or any oversized unit)
    // intercepts if ANY cell it occupies is the mover's path cell.
    const occupied = unitFootprintCells(card, cellAt(mesh.position.x, mesh.position.z));
    if (!occupied.some((c) => c.col === cell.col && c.row === cell.row)) continue;
    // Opponent-controlled (rotated convention matches the rest of the engine).
    if (!!card.rotated === moverRotated) continue;
    if (card.tapped) continue;
    // Intercept taps as its cost. A summoning-sick unit (entered the realm
    // this turn) can't pay it. Mirrors server filterInterceptCandidates.
    if (hasSummoningSickness(card)) continue;
    if (hasKeyword(card, 'disabled')) continue;
    // Printed "Can't defend or intercept" (Lumbering Giant).
    if (hasKeyword(card, 'cant_intercept')) continue;
    if ((card.subsurface || null) !== moverRegion) continue;

    const canReachAir = hasKeyword(card, 'airborne') || getRangedValue(card) > 0;
    if (moverAirborne && !canReachAir) continue;

    out.push(card);
  }
  return out;
}
